// Long work started from a page (painting a picture, drawing an animation's frames) keeps going when the page closes:
// its result is written into the episode wherever it lives (see liveEpisodes.ts). While it runs it is listed here
// under a key (like "pic:<marker id>"), so a page opened again shows it is still going, and the script can show it on
// the row it belongs to. Something that fails after its page has closed is told through `notify`, and the editor
// shows it as a toast. A message sent while nothing is listening waits for the next listener.
// All of it lives on globalThis, so a reload of the app's code doesn't lose running work or its messages.
import { useSyncExternalStore } from "react";

const g = globalThis as { sketchyJobs?: { running: Map<string, number>; listeners: Set<() => void>; noticeListeners: Set<(message: string) => void>; waiting: string[]; snapshot: ReadonlyMap<string, number> } };
const s = (g.sketchyJobs ??= { running: new Map(), listeners: new Set(), noticeListeners: new Set(), waiting: [] as string[], snapshot: new Map() });
const { running, listeners, noticeListeners } = s; // running: key -> when it started

function changed() {
  s.snapshot = new Map(running);
  for (const l of listeners) l();
}

// Runs `work` under `key` until it settles. The same promise comes back.
export function runJob<T>(key: string, work: Promise<T>): Promise<T> {
  running.set(key, Date.now());
  changed();
  void work.catch(() => {}).finally(() => { running.delete(key); changed(); });
  return work;
}

const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };
const all = () => s.snapshot;

// When the job under `key` started, or null if none is running.
export function useJob(key: string | undefined): number | null {
  const jobs = useSyncExternalStore(subscribe, all);
  return key ? jobs.get(key) ?? null : null;
}
// Whether a job is running under `key` (outside React).
export const isRunning = (key: string) => running.has(key);
// Every running job's key.
export function useJobs(): ReadonlyMap<string, number> {
  return useSyncExternalStore(subscribe, all);
}

// A message about background work for whoever is listening (the editor's toast).
export function notify(message: string) {
  console.warn(message);
  if (!noticeListeners.size) { s.waiting.push(message); return; }
  for (const l of noticeListeners) l(message);
}
export function onNotice(l: (message: string) => void): () => void {
  noticeListeners.add(l);
  for (const m of s.waiting.splice(0)) l(m);
  return () => { noticeListeners.delete(l); };
}

export const picJob = (markerId: string) => `pic:${markerId}`;
export const motionJob = (markerId: string) => `motion:${markerId}`;
export const lookJob = (assetId: string) => `look:${assetId}`;
